/**
 * No Relief reminders — which unticked calls are close enough to their time that
 * the user should be nagged to make them.
 *
 * A call is due from REMINDER_LEAD_SLOTS (30 minutes) before the counter goes
 * unmanned until REMINDER_GRACE_SLOTS (15 minutes) after — long past that the
 * moment has gone and the list in the No Relief modal is the place for it. Only
 * "no relief" calls count; "relief is coming now" is a correction, not a deadline.
 */

import type { NoReliefEntry, NoReliefGroup } from '@/utils/noRelief';

/** 30 minutes. */
export const REMINDER_LEAD_SLOTS = 2;
/** 15 minutes. */
export const REMINDER_GRACE_SLOTS = 1;
/** A closed reminder comes back after this long unless every call in it is ticked. */
export const REMINDER_SNOOZE_MS = 2 * 60 * 1000;

export interface DueReminderEntry {
  entry: NoReliefEntry;
  /** Officers standing on the counter until the slot — the ones to phone. */
  officerIds: string[];
}

export interface DueReminderGroup {
  slot: number;
  /** Minutes until the counter goes unmanned; negative once it has. */
  minutesAway: number;
  entries: DueReminderEntry[];
}

export const reminderKey = (e: { counter: number; slot: number }) => `${e.counter}:${e.slot}`;

/**
 * @param nowSlot the live time as a fractional slot from the shift's left edge,
 *   or null when outside the shift (nothing is due then).
 */
export function dueReminders(
  groups: NoReliefGroup[],
  counterMatrix: string[][][] | undefined,
  nowSlot: number | null,
): DueReminderGroup[] {
  if (nowSlot === null || !counterMatrix) return [];
  const out: DueReminderGroup[] = [];
  for (const group of groups) {
    if (nowSlot < group.slot - REMINDER_LEAD_SLOTS || nowSlot >= group.slot + REMINDER_GRACE_SLOTS) continue;
    const entries = group.entries
      .filter((e) => e.current && e.pending === 'no_relief')
      .map((entry) => ({ entry, officerIds: counterMatrix[entry.counter]?.[entry.slot - 1] ?? [] }));
    if (entries.length > 0) out.push({ slot: group.slot, minutesAway: Math.round((group.slot - nowSlot) * 15), entries });
  }
  return out;
}
